/* ============================================================
   SINGSPACE ADMIN - TABLEAU ADMINS (Pro Max)
   ============================================================ */

import { AlertCircle, Clock, Edit2, SearchX, Shield, ShieldCheck, User, UserX } from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';

interface AdminsTableProps {
  users: any[];
  loading: boolean;
  error: string | null;
  statusFilter: 'all' | 'active' | 'inactive';
  onEdit: (user: any) => void;
  onDelete: (id: string) => void;
}

export function AdminsTable({ users, loading, error, statusFilter, onEdit, onDelete }: AdminsTableProps) {
  const filteredUsers = users.filter((user) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return user.isActive;
    if (statusFilter === 'inactive') return !user.isActive;
    return true;
  });

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (filteredUsers.length === 0) return <EmptyState title="Aucun administrateur trouvé" />;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-slate-50/50 border-b border-gray-100">
            <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
              Profil Administrateur
            </th>
            <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
              Rôle & Droits
            </th>
            <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
              Statut
            </th>
            <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
              Dernière connexion
            </th>
            <th className="px-6 py-4 text-right text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {filteredUsers.map((admin) => (
            <tr key={admin.id} className="hover:bg-slate-50/30 transition-colors group">
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#00303c]/5 text-[#00303c] flex items-center justify-center font-black text-xs border border-[#00303c]/10">
                    {admin.name
                      .split(' ')
                      .map((n: string) => n[0])
                      .join('')
                      .toUpperCase()
                      .substring(0, 2)}
                  </div>
                  <div className="flex flex-col">
                    <span className="font-bold text-[#00303c] text-sm leading-tight">{admin.name}</span>
                    <span className="text-xs text-slate-400 font-medium">{admin.email}</span>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-100 border border-slate-200 text-[#00303c]">
                  {getRoleIcon(admin.role)}
                  <span className="text-[10px] font-black uppercase tracking-wider">{getRoleLabel(admin.role)}</span>
                </div>
              </td>
              <td className="px-6 py-4">
                <div
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${admin.isActive ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'}`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${admin.isActive ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`}
                  />
                  {admin.isActive ? 'Actif' : 'Inactif'}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-2 text-slate-500 font-medium text-xs">
                  <Clock size={14} className="opacity-40" />
                  {admin.lastLogin
                    ? new Date(admin.lastLogin).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : 'Aucune session'}
                </div>
              </td>
              <td className="px-6 py-4 text-right">
                <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(admin)}
                    className="h-9 w-9 rounded-xl text-[#00758d] hover:bg-[#00758d]/10"
                  >
                    <Edit2 size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(admin.id)}
                    className="h-9 w-9 rounded-xl text-rose-400 hover:text-rose-600 hover:bg-rose-50"
                  >
                    <UserX size={16} />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function getRoleLabel(role: string) {
  switch (role) {
    case 'SUPER_ADMIN':
      return 'Super Admin';
    case 'ADMIN':
      return 'Administrateur';
    case 'MANAGER':
      return 'Manager';
    default:
      return role;
  }
}

function getRoleIcon(role: string) {
  switch (role) {
    case 'SUPER_ADMIN':
      return <ShieldCheck size={14} />;
    case 'ADMIN':
      return <Shield size={14} />;
    default:
      return <User size={14} />;
  }
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <LoadingSpinner message="Chargement des données..." submessage="" size="md" />
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-rose-500">
      <AlertCircle size={40} className="mb-4 opacity-50" />
      <h3 className="font-black uppercase tracking-widest text-xs mb-1">Erreur de chargement</h3>
      <p className="text-sm font-medium opacity-70">{message}</p>
    </div>
  );
}

function EmptyState({ title }: { title: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-slate-300">
      <SearchX size={48} className="mb-4 opacity-20" />
      <h3 className="font-black uppercase tracking-widest text-xs mb-1">{title}</h3>
      <p className="text-xs font-medium opacity-50 text-center max-w-xs">
        Aucun résultat ne correspond à vos filtres actuels.
      </p>
    </div>
  );
}
